import axios from "axios";
import { cacheGet, readGetCache, queueMutation, getOnlineState } from "@/lib/offline";

export const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Attach token from localStorage as fallback for non-cookie environments
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("ac_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Endpoints que NO conviene cachear offline (binarios, descargas)
const SKIP_OFFLINE_CACHE = [
  "/desktop/download",
  "/attachment",
  "/pdf",
  "/ticket",
  "/clinical-report",
  "/auth/",
];

function shouldCache(url) {
  if (!url) return false;
  return !SKIP_OFFLINE_CACHE.some((p) => url.includes(p));
}

function buildCacheKey(cfg) {
  // Clave única por usuario para evitar fugas entre sesiones
  const token = (typeof localStorage !== "undefined" && localStorage.getItem("ac_token")) || "anon";
  const userKey = token.slice(-12);
  const u = (cfg.url || "").startsWith("http") ? cfg.url : `${API_BASE}${cfg.url}`;
  const params = cfg.params ? JSON.stringify(cfg.params) : "";
  return `${userKey}|${u}|${params}`;
}

// Cachear respuestas GET y devolver cache cuando offline ----------------
api.interceptors.response.use(
  (response) => {
    try {
      if (response.config?.method === "get" && response.status >= 200 && response.status < 300) {
        const cfg = response.config;
        const respType = cfg.responseType || "json";
        if (respType === "json" && shouldCache(cfg.url)) {
          cacheGet(buildCacheKey(cfg), response.data);
        }
      }
    } catch {
      /* swallow cache errors */
    }
    return response;
  },
  async (error) => {
    const cfg = error.config;
    const isNetworkError = !error.response || error.code === "ERR_NETWORK" || !getOnlineState();
    if (!cfg || !isNetworkError) {
      return Promise.reject(error);
    }
    const method = (cfg.method || "get").toLowerCase();
    if (method === "get") {
      if ((cfg.responseType || "json") === "json" && shouldCache(cfg.url)) {
        const cached = await readGetCache(buildCacheKey(cfg));
        if (cached) {
          return {
            data: cached.data,
            status: 200,
            statusText: "OK (offline cache)",
            headers: { "x-offline-cache": "true", "x-offline-cached-at": new Date(cached.ts).toISOString() },
            config: cfg,
            request: null,
          };
        }
      }
      return Promise.reject(error);
    }
    // Mutación → encolar para reintento
    try {
      queueMutation({
        method,
        url: cfg.url,
        data: cfg.data ? (typeof cfg.data === "string" ? JSON.parse(cfg.data) : cfg.data) : null,
        headers: cfg.headers
          ? Object.fromEntries(Object.entries(cfg.headers).filter(([k]) => !k.toLowerCase().startsWith("authorization")))
          : null,
      });
      // Devolvemos 202 simulado para que el llamador pueda continuar (UI no rompe)
      return {
        data: { queued_offline: true, message: "Guardado offline. Se enviará al volver internet." },
        status: 202,
        statusText: "Queued offline",
        headers: { "x-offline-queued": "true" },
        config: cfg,
        request: null,
      };
    } catch (queueErr) {
      return Promise.reject(
        new Error(
          queueErr?.message?.includes("multipart")
            ? "Estás offline y no se pueden encolar subidas de archivos. Vuelve a intentarlo con conexión."
            : "Estás offline. No se pudo guardar este cambio."
        )
      );
    }
  },
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Ha ocurrido un error";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  if (typeof detail === "object" && detail.message) return detail.message;
  return JSON.stringify(detail);
}

export function euro(v) {
  const n = Number(v) || 0;
  return n.toLocaleString("es-ES", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    useGrouping: "always",
  });
}

export function formatDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    if (isNaN(d.getTime())) return s;
    return d.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return s;
  }
}
